import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Brain,
  CheckCircle,
  AlertTriangle,
  Clock,
  TrendingUp,
  FileText,
  Settings,
  AlertCircle,
  Zap,
  Target,
  Activity,
  BarChart3,
  Edit,
  Eye,
  EyeOff,
  ThumbsUp,
  ThumbsDown,
  Package,
  Users,
  CreditCard
} from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import { api } from '@/services/api';

interface CorrectionRule {
  id: string;
  name: string;
  description: string;
  category: string;
  type: string;
  severity: string;
  pattern: string;
  action: any;
  isActive: boolean;
  priority: number;
  autoApply: boolean;
  successRate: number;
  totalApplications: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

interface Correction {
  id: string;
  ruleId: string;
  entityType: string;
  entityId: string;
  originalValue: any;
  correctedValue?: any;
  suggestion?: string;
  type: string;
  severity: string;
  category: string;
  status: string;
  confidence: number;
  appliedAt?: string;
  appliedBy?: string;
  rejectedAt?: string;
  rejectedBy?: string;
  expiresAt?: string;
  metadata: any;
  createdAt: string;
}

interface Analytics {
  totalCorrections: number;
  correctionsByType: Record<string, number>;
  correctionsByCategory: Record<string, number>;
  correctionsBySeverity: Record<string, number>;
  averageConfidence: number;
  topRules: Array<{
    ruleId: string;
    ruleName: string;
    applications: number;
    successRate: number;
  }>;
  recentActivity: Array<{
    date: string;
    corrections: number;
    applications: number;
    rejections: number;
  }>;
  errorPatterns: Array<{
    pattern: string;
    frequency: number;
    category: string;
    suggestedAction: string;
  }>;
}

const SmartCorrectionsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'rules' | 'corrections' | 'analytics' | 'settings'>('dashboard');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const queryClient = useQueryClient();

  // Fetch dashboard data
  const { data: dashboardData } = useQuery({
    queryKey: ['smart-corrections', 'dashboard'],
    queryFn: async () => {
      const res = await api.get('/api/smart-corrections/dashboard');
      return res.data.data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch correction rules
  const { data: rulesData } = useQuery<{
    rules: CorrectionRule[];
    count: number;
  }>({
    queryKey: ['smart-corrections', 'rules'],
    queryFn: async () => {
      const res = await api.get('/api/smart-corrections/rules');
      return res.data;
    },
  });

  // Fetch corrections
  const { data: correctionsData } = useQuery<{
    data: Correction[];
    pagination: any;
  }>({
    queryKey: ['smart-corrections', 'corrections'],
    queryFn: async () => {
      const res = await api.get('/api/smart-corrections/corrections');
      return res.data;
    },
    refetchInterval: 15000, // Refresh every 15 seconds
  });

  // Fetch analytics
  const { data: analyticsData } = useQuery<Analytics>({
    queryKey: ['smart-corrections', 'analytics'],
    queryFn: async () => {
      const res = await api.get('/api/smart-corrections/analytics');
      return res.data.data;
    },
    refetchInterval: 60000, // Refresh every minute
  });

  // Apply correction mutation
  const applyCorrectionMutation = useMutation({
    mutationFn: async (correctionId: string) => {
      const res = await api.post(`/api/smart-corrections/corrections/${correctionId}/apply`);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Correction applied successfully');
      queryClient.invalidateQueries({ queryKey: ['smart-corrections'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to apply correction');
    },
  });

  // Reject correction mutation
  const rejectCorrectionMutation = useMutation({
    mutationFn: async (correctionId: string) => {
      const res = await api.post(`/api/smart-corrections/corrections/${correctionId}/reject`);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Correction rejected');
      queryClient.invalidateQueries({ queryKey: ['smart-corrections'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to reject correction');
    },
  });

  // Toggle rule mutation
  const toggleRuleMutation = useMutation({
    mutationFn: async ({ ruleId, isActive }: { ruleId: string; isActive: boolean }) => {
      const res = await api.put(`/api/smart-corrections/rules/${ruleId}`, { isActive });
      return res.data;
    },
    onSuccess: () => {
      toast.success('Rule updated successfully');
      queryClient.invalidateQueries({ queryKey: ['smart-corrections', 'rules'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update rule');
    },
  });

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      case 'high':
        return <AlertTriangle className="h-4 w-4 text-orange-600" />;
      case 'medium':
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      case 'low':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-600" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'pricing':
        return <Target className="h-4 w-4" />;
      case 'data_entry':
        return <Edit className="h-4 w-4" />;
      case 'tax':
        return <FileText className="h-4 w-4" />;
      case 'product':
        return <Package className="h-4 w-4" />;
      case 'customer':
        return <Users className="h-4 w-4" />;
      case 'payment':
        return <CreditCard className="h-4 w-4" />;
      case 'inventory':
        return <Package className="h-4 w-4" />;
      default:
        return <Settings className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Smart Corrections</h1>
          <p className="mt-1 text-sm text-gray-600">
            AI-powered data quality and error correction system
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="primary"
          >
            <Brain className="h-4 w-4 mr-2" />
            Create Rule
          </Button>
        </div>
      </div>

      {/* Status Overview */}
      {dashboardData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100">Active Corrections</p>
                <p className="text-3xl font-bold mt-2">{dashboardData.summary.activeCorrections}</p>
              </div>
              <Activity className="h-8 w-8 text-blue-200" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-500 to-teal-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100">Success Rate</p>
                <p className="text-3xl font-bold mt-2">
                  {dashboardData.summary.averageConfidence ? 
                    `${(dashboardData.summary.averageConfidence * 100).toFixed(1)}%` : 
                    'N/A'
                  }
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-200" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-orange-500 to-red-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100">Total Rules</p>
                <p className="text-3xl font-bold mt-2">{dashboardData.summary.totalRules}</p>
              </div>
              <Zap className="h-8 w-8 text-orange-200" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100">Engine Status</p>
                <p className="text-3xl font-bold mt-2">
                  {dashboardData.summary.enabled ? 'Active' : 'Inactive'}
                </p>
              </div>
              <Brain className="h-8 w-8 text-purple-200" />
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-lg">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
              { id: 'rules', label: 'Rules', icon: Settings },
              { id: 'corrections', label: 'Corrections', icon: CheckCircle },
              { id: 'analytics', label: 'Analytics', icon: TrendingUp },
              { id: 'settings', label: 'Settings', icon: Settings },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-4 w-4 inline mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'dashboard' && dashboardData && (
            <div className="space-y-6">
              {/* Recent Corrections */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Corrections</h3>
                <div className="space-y-3">
                  {dashboardData.pendingCorrections?.slice(0, 5).map((correction: any) => (
                    <div key={correction.id} className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          {getSeverityIcon(correction.severity)}
                          <div>
                            <p className="font-medium text-gray-900">{correction.metadata.ruleName}</p>
                            <p className="text-sm text-gray-600">
                              {correction.entityType} - {correction.suggestion}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSeverityColor(correction.severity)}`}>
                            {correction.severity}
                          </span>
                          <div className="flex space-x-1">
                            <button
                              onClick={() => applyCorrectionMutation.mutate(correction.id)}
                              className="p-1 text-green-600 hover:bg-green-50 rounded"
                              title="Apply"
                            >
                              <ThumbsUp className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => rejectCorrectionMutation.mutate(correction.id)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                              title="Reject"
                            >
                              <ThumbsDown className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Analytics Summary */}
              {analyticsData && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Corrections by Category</h3>
                    <div className="space-y-2">
                      {Object.entries(analyticsData.correctionsByCategory).map(([category, count]) => (
                        <div key={category} className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            {getCategoryIcon(category)}
                            <span className="text-sm font-medium text-gray-700 capitalize">
                              {category.replace('_', ' ')}
                            </span>
                          </div>
                          <span className="text-sm text-gray-600">{count as number}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Top Performing Rules</h3>
                    <div className="space-y-2">
                      {analyticsData.topRules?.slice(0, 5).map((rule) => (
                        <div key={rule.ruleId} className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">{rule.ruleName}</span>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-600">{rule.applications} uses</span>
                            <span className="text-xs text-green-600">
                              {(rule.successRate * 100).toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'rules' && rulesData && (
            <div className="space-y-6">
              {/* Category Filter */}
              <div className="flex items-center space-x-4">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Categories</option>
                  <option value="pricing">Pricing</option>
                  <option value="data_entry">Data Entry</option>
                  <option value="tax">Tax</option>
                  <option value="product">Product</option>
                  <option value="customer">Customer</option>
                  <option value="payment">Payment</option>
                  <option value="inventory">Inventory</option>
                </select>
              </div>

              {/* Rules List */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rule</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Success Rate</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {rulesData.rules
                      .filter((rule: any) => selectedCategory === 'all' || rule.category === selectedCategory)
                      .map((rule: any) => (
                        <tr key={rule.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <p className="font-medium text-gray-900">{rule.name}</p>
                              <p className="text-sm text-gray-500">{rule.description}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              {getCategoryIcon(rule.category)}
                              <span className="text-sm text-gray-900 capitalize">
                                {rule.category.replace('_', ' ')}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-900 capitalize">{rule.type}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSeverityColor(rule.severity)}`}>
                              {rule.severity}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <span className="text-sm text-gray-900">
                                {(rule.successRate * 100).toFixed(1)}%
                              </span>
                              <div className="ml-2 w-16 bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-green-600 h-2 rounded-full"
                                  style={{ width: `${rule.successRate * 100}%` }}
                                />
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => toggleRuleMutation.mutate({ ruleId: rule.id, isActive: !rule.isActive })}
                              className={`p-1 rounded ${rule.isActive ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-50'}`}
                            >
                              {rule.isActive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <button className="text-blue-600 hover:text-blue-900 mr-3">Edit</button>
                            <button className="text-red-600 hover:text-red-900">Delete</button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'corrections' && correctionsData && (
            <div className="space-y-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entity</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rule</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Original</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Suggested</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Confidence</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {correctionsData.data.map((correction: any) => (
                      <tr key={correction.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <p className="font-medium text-gray-900">{correction.entityType}</p>
                            <p className="text-sm text-gray-500">{correction.entityId}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <p className="text-sm text-gray-900">{correction.metadata.ruleName}</p>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <p className="text-sm text-gray-900">{correction.originalValue}</p>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <p className="text-sm text-gray-900">
                            {correction.correctedValue || correction.suggestion}
                          </p>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className="text-sm text-gray-900">
                              {(correction.confidence * 100).toFixed(1)}%
                            </span>
                            <div className="ml-2 w-16 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-blue-600 h-2 rounded-full"
                                style={{ width: `${correction.confidence * 100}%` }}
                              />
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            correction.status === 'applied' ? 'bg-green-100 text-green-800' :
                            correction.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {correction.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {correction.status === 'pending' && (
                            <div className="flex space-x-2">
                              <button
                                onClick={() => applyCorrectionMutation.mutate(correction.id)}
                                className="text-green-600 hover:text-green-900"
                              >
                                Apply
                              </button>
                              <button
                                onClick={() => rejectCorrectionMutation.mutate(correction.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Reject
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'analytics' && analyticsData && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Total Corrections</h3>
                  <p className="text-3xl font-bold text-blue-600">{analyticsData.totalCorrections}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Average Confidence</h3>
                  <p className="text-3xl font-bold text-green-600">
                    {(analyticsData.averageConfidence * 100).toFixed(1)}%
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Active Rules</h3>
                  <p className="text-3xl font-bold text-purple-600">
                    {rulesData?.rules?.filter((r: any) => r.isActive).length || 0}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Corrections by Type</h3>
                  <div className="space-y-2">
                    {Object.entries(analyticsData.correctionsByType).map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700 capitalize">{type}</span>
                        <span className="text-sm text-gray-600">{count as number}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Corrections by Severity</h3>
                  <div className="space-y-2">
                    {Object.entries(analyticsData.correctionsBySeverity).map(([severity, count]) => (
                      <div key={severity} className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          {getSeverityIcon(severity)}
                          <span className="text-sm font-medium text-gray-700 capitalize">{severity}</span>
                        </div>
                        <span className="text-sm text-gray-600">{count as number}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Smart Corrections Settings</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">Enable Smart Corrections</p>
                      <p className="text-sm text-gray-600">Turn on/off the smart corrections engine</p>
                    </div>
                    <button className="relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                      <span className="translate-x-5 inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"></span>
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">Auto-apply Corrections</p>
                      <p className="text-sm text-gray-600">Automatically apply high-confidence corrections</p>
                    </div>
                    <button className="relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                      <span className="translate-x-0 inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"></span>
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">Confidence Threshold</p>
                      <p className="text-sm text-gray-600">Minimum confidence level for suggestions</p>
                    </div>
                    <input
                      type="number"
                      min="0"
                      max="1"
                      step="0.1"
                      defaultValue="0.8"
                      className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmartCorrectionsPage;
